import { QA_API_DOMAIN, fetchJsonWithSupabaseAuth, fetchWithSupabaseAuth } from "@/api/classifier";
import type { Database } from "@/integrations/supabase/types";

export interface ReviewableCountQuery {
  admit_date_from?: string;
  admit_date_to?: string;
  customer_ids?: string;
}

type Specialty = Database["public"]["Enums"]["encounter_speciality_type"];
export type ReviewableCountBySpecialty = Partial<Record<Specialty, number>>;
export type ReviewableCount = ReviewableCountBySpecialty & { total: number };

const REVIEWABLE_COUNT_URL = `${QA_API_DOMAIN}/reviews/reviewable-count`;
const VOID_ENCOUNTER_REVIEW_URL = `${QA_API_DOMAIN}/reviews/void-encounter-review`;
const UNVOID_ENCOUNTER_REVIEW_URL = `${QA_API_DOMAIN}/reviews/unvoid-encounter-review`;
const ACCEPT_CLASSIFICATION_RESULT_URL = `${QA_API_DOMAIN}/reviews/accept-classification-result`;

export interface AcceptClassificationResultRequest {
  inbound_classification_result_id: string;
  reason: string;
}

export interface AcceptClassificationResultResponse {
  ok: true;
  inbound_classification_result_id: string;
}

export async function acceptClassificationResult(
  body: AcceptClassificationResultRequest,
): Promise<AcceptClassificationResultResponse> {
  const res = await fetchWithSupabaseAuth(ACCEPT_CLASSIFICATION_RESULT_URL, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });

  if (res.ok) {
    return res.json() as Promise<AcceptClassificationResultResponse>;
  }

  const errorBody = (await res.json().catch(() => ({}))) as { error?: string };
  const message = errorBody.error ?? `Request failed (${res.status}).`;
  throw new Error(message);
}

const ADD_TO_GOLDEN_DATASET_URL = `${QA_API_DOMAIN}/reviews/add-to-golden-dataset`;

export type GoldenDatasetSource = { kind: "ai_result" | "human_review"; id: string };

export type GoldenDatasetCodes = { diagnoses: string[]; procedures: string[] };

export interface AddToGoldenDatasetRequest {
  encounterId: string;
  source: GoldenDatasetSource;
  confirmedCodes: GoldenDatasetCodes;
}

export interface AddToGoldenDatasetResponse {
  datasetId: string;
  outcome: "created" | "updated";
}

export async function addToGoldenDataset(body: AddToGoldenDatasetRequest): Promise<AddToGoldenDatasetResponse> {
  return fetchJsonWithSupabaseAuth(ADD_TO_GOLDEN_DATASET_URL, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ ...body, confirmedAllCodesCorrect: true }),
  });
}

export type GoldenDatasetStatus =
  | { inDataset: false }
  | {
      inDataset: true;
      datasetId: string;
      source: GoldenDatasetSource | null;
      addedBy: string | null;
      addedAt: string | null;
    };

export interface RemoveFromGoldenDatasetResponse {
  datasetId: string;
}

export async function getGoldenDatasetStatus(encounterId: string): Promise<GoldenDatasetStatus> {
  return fetchJsonWithSupabaseAuth(`${QA_API_DOMAIN}/reviews/golden-dataset-status/${encodeURIComponent(encounterId)}`);
}

export async function removeFromGoldenDataset(encounterId: string): Promise<RemoveFromGoldenDatasetResponse> {
  return fetchJsonWithSupabaseAuth(`${QA_API_DOMAIN}/reviews/remove-from-golden-dataset`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ encounterId }),
  });
}

export interface VoidEncounterReviewRequest {
  encounter_id: string;
  human_review_id: string;
}

export interface VoidEncounterReviewResponse {
  ok: true;
  human_review_id: string;
}

function userMessageForStatus(status: number, endpoint: "void" | "unvoid"): string {
  switch (status) {
    case 403:
      return "You don't have permission to void/unvoid reviews.";
    case 404:
      return "Review not found.";
    case 409:
      return endpoint === "void" ? "Review is already voided." : "Review is not voided.";
    case 401:
      return "Please sign in again.";
    case 400:
      return "Invalid request.";
    case 500:
      return "Something went wrong. Please try again.";
    default:
      return `Request failed (${status}).`;
  }
}

async function voidOrUnvoid(
  url: string,
  body: VoidEncounterReviewRequest,
  endpoint: "void" | "unvoid",
): Promise<VoidEncounterReviewResponse> {
  const res = await fetchWithSupabaseAuth(url, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });

  if (res.ok) {
    return res.json() as Promise<VoidEncounterReviewResponse>;
  }

  const errorBody = (await res.json().catch(() => ({}))) as { error?: string };
  const message = errorBody.error ?? userMessageForStatus(res.status, endpoint);
  throw new Error(message);
}

export async function voidEncounterReview(body: VoidEncounterReviewRequest): Promise<VoidEncounterReviewResponse> {
  return voidOrUnvoid(VOID_ENCOUNTER_REVIEW_URL, body, "void");
}

export async function unvoidEncounterReview(body: VoidEncounterReviewRequest): Promise<VoidEncounterReviewResponse> {
  return voidOrUnvoid(UNVOID_ENCOUNTER_REVIEW_URL, body, "unvoid");
}

export async function getReviewableCount(query?: ReviewableCountQuery): Promise<ReviewableCount> {
  const params = new URLSearchParams();
  if (query?.admit_date_from) params.set("admit_date_from", query.admit_date_from);
  if (query?.admit_date_to) params.set("admit_date_to", query.admit_date_to);
  if (query?.customer_ids) params.set("customer_ids", query.customer_ids);

  const url = `${REVIEWABLE_COUNT_URL}${params.toString() ? `?${params}` : ""}`;
  const res = await fetchWithSupabaseAuth(url, {
    method: "GET",
    headers: { "Content-Type": "application/json" },
  });

  if (res.status === 401) {
    throw new Error("Unauthorized: missing user context");
  }
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw new Error((body as { error?: string }).error ?? `HTTP ${res.status}`);
  }

  const data: ReviewableCount = await res.json();
  return data;
}
